export type NodeType = 'Human' | 'Project' | 'Occupation';
export type ImageSource = 'upload' | 'preset-avatar' | 'fallback';
export type CustomFieldValueType = 'text' | 'longtext' | 'url' | 'email' | 'number' | 'date';

export interface CustomFieldEntry {
  fieldId: string;
  label: string;
  value: string;
  valueType: CustomFieldValueType;
  displayOrder: number;
}

export interface BaseNode {
  nodeId: string;
  type: NodeType;
  displayName: string;
  summary?: string;
  tags: string[];
  imageAssetId: string | null;
  imageSource: ImageSource;
  presetAvatarId: string | null;
  notes: string;
  customFields: CustomFieldEntry[];
  createdAt: string;
  createdAtTimeZone?: string;
  updatedAt: string;
  updatedAtTimeZone?: string;
}

export interface HumanNode extends BaseNode {
  type: 'Human';
  occupationIds: string[];
  projectIds: string[];
  relatedHumanIds: string[];
}
export interface ProjectNode extends BaseNode {
  type: 'Project';
  participantHumanIds: string[];
  relatedOccupationIds: string[];
  relatedProjectIds: string[];
}
export interface OccupationNode extends BaseNode {
  type: 'Occupation';
  relatedHumanIds: string[];
  typicalProjectIds: string[];
}
export type JourneyNode = HumanNode | ProjectNode | OccupationNode;

export function nowIso(): string { return new Date().toISOString(); }
export function currentTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}
export function createBaseNode(input: Partial<BaseNode> & Pick<BaseNode, 'nodeId' | 'type' | 'displayName'>): BaseNode {
  const now = input.createdAt ?? nowIso();
  const timeZone = input.createdAtTimeZone ?? input.updatedAtTimeZone ?? currentTimeZone();
  return {
    summary: '',
    tags: [],
    imageAssetId: null,
    imageSource: 'fallback',
    presetAvatarId: null,
    notes: '',
    customFields: [],
    updatedAt: now,
    updatedAtTimeZone: input.updatedAtTimeZone ?? input.createdAtTimeZone ?? timeZone,
    createdAt: now,
    createdAtTimeZone: timeZone,
    ...input,
  };
}
export function createHumanNode(input: Partial<HumanNode> & Pick<HumanNode, 'nodeId' | 'displayName'>): HumanNode { return { ...createBaseNode({ ...input, type: 'Human' }), occupationIds: [], projectIds: [], relatedHumanIds: [], ...input, type: 'Human' }; }
export function createProjectNode(input: Partial<ProjectNode> & Pick<ProjectNode, 'nodeId' | 'displayName'>): ProjectNode { return { ...createBaseNode({ ...input, type: 'Project' }), participantHumanIds: [], relatedOccupationIds: [], relatedProjectIds: [], ...input, type: 'Project' }; }
export function createOccupationNode(input: Partial<OccupationNode> & Pick<OccupationNode, 'nodeId' | 'displayName'>): OccupationNode { return { ...createBaseNode({ ...input, type: 'Occupation' }), relatedHumanIds: [], typicalProjectIds: [], ...input, type: 'Occupation' }; }
export function isNodeComplete(node: JourneyNode): boolean { return node.displayName.trim().length > 0; }
